import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import QuiltCalculatorPreview from './QuiltCalculatorPreview';

const AboutSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqData = [
    {
      question: "What is NOSTAi?",
      answer: "NOSTAi is an AI-powered design service that creates personalized memory quilt layouts using your cherished t-shirts and baby clothes. It turns your sentimental garments into beautifully crafted quilt designs, preserving your most meaningful moments in a creative and lasting way."
    },
    {
      question: "How does NOSTAi work?",
      answer: (
        <div>
          <p>Here's how NOSTAi helps turn your cherished t-shirts and baby clothes into a memory quilt:</p>
          <ul className="list-disc pl-6 mt-2">
            <li className="mb-2"><span className="font-medium">Upload your clothing images</span><br/>Share clear photos of your special t-shirts or baby clothes—front and back.</li>
            <li className="mb-2"><span className="font-medium">AI-powered analysis</span><br/>NOSTAi's smart AI scans each image to identify colors, graphics, text, and unique patterns.</li>
            <li className="mb-2"><span className="font-medium">Personalized quilt layouts</span><br/>Based on the number of clothing pieces, NOSTAi creates 3–5 layout options tailored to your preferences and design style.</li>
            <li><span className="font-medium">Pick your favorite design</span><br/>Choose the layout you love most, and we'll guide you on the next steps to bring your memory quilt to life.</li>
          </ul>
        </div>
      )
    },
    {
      question: "How many t-shirts or clothing pieces do I need?",
      answer: (
        <div>
          <p>The number of items you'll need depends on the quilt size and tile dimensions you choose. Each t-shirt usually provides two usable sides—front and back—giving you more flexibility in design.</p>
          
          <div className="mt-2">
            <p className="font-semibold">For Larger Tiles (11–12 inches):</p>
            <ul className="list-disc pl-6 mt-1 mb-2">
              <li>Twin Quilt (4' x 6') – 4x6 layout → 24 t-shirt sides</li>
              <li>Full Quilt (5' x 7') – 5x7 layout → 35 t-shirt sides</li>
              <li>Large Throw (7' x 8') – 7x8 layout → 56 t-shirt sides</li>
            </ul>
            
            <p className="font-semibold">For Smaller Tiles (9–10 inches):</p>
            <ul className="list-disc pl-6 mt-1 mb-2">
              <li>Twin Quilt (4' x 6') – 5x7 layout → 35 t-shirt sides</li>
              <li>Full Quilt (5' x 7') – 6x8 layout → 48 t-shirt sides</li>
              <li>Large Throw (7' x 8') – 8x9 layout → 72 t-shirt sides</li>
            </ul>
          </div>
          
          <p className="mt-2">✨ <span className="font-medium">Need help figuring it out?</span><br/>
          Use our Memory Design Calculator to quickly determine the ideal quilt size based on the number of t-shirts or baby clothes you have.</p>
        </div>
      )
    },
    {
      question: "Do I receive the finished quilt from NOSTAi?",
      answer: "No, NOSTAi focuses on designing your personalized memory quilt layout using your cherished t-shirts or baby clothes. Once you choose a design you love, you can collaborate with a quilt maker—like Sweet Root Memory Quilter—to craft the actual quilt.\n\nWe'll guide you through the next steps to ensure a smooth and stress-free experience from design to finished keepsake."
    },
    {
      question: "Why should I use NOSTAi instead of designing on my own?",
      answer: (
        <div>
          <p>Designing a memory quilt can be overwhelming—but NOSTAi makes it beautifully simple. Here's why:</p>
          <ul className="list-disc pl-6 mt-2">
            <li>Professionally balanced layouts crafted using AI intelligence</li>
            <li>Thoughtful arrangement of colors, graphics, and sentimental motifs</li>
            <li>Multiple design options tailored to your style and preferences</li>
            <li>Saves time and effort, making the process easy and stress-free</li>
          </ul>
          <p className="mt-2">Whether you're preserving baby clothes, t-shirts, or a mix of memories—NOSTAi helps you turn them into a stunning, meaningful quilt with confidence.</p>
        </div>
      )
    },
    {
      question: "Can I customize the layout further?",
      answer: (
        <div>
          <p>Absolutely! With NOSTAi, you're not limited to the suggested layouts. You can:</p>
          <ul className="list-disc pl-6 mt-2">
            <li>Customize or edit the design to match your preferences</li>
            <li>Add a border for a finished, framed look</li>
            <li>Include a name or personal message for that extra special touch</li>
            <li>Preserve uncut garments with our emboss (uncut) tile option for clothes you want to keep whole</li>
          </ul>
          <p className="mt-2">Your quilt design is as unique as your memories—and NOSTAi gives you the tools to make it truly personal.</p>
        </div>
      )
    },
    {
      question: "Is NOSTAi available globally?",
      answer: "Yes! NOSTAi is a global design service. No matter where you are in the world, you can access our platform to create beautiful memory quilt layouts using your cherished t-shirts, baby clothes, or other garments. Design from anywhere—your memories know no borders."
    }
  ];

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="flex flex-col justify-center w-full max-w-[994px] px-5 mx-auto">
      {/* About Section */}
      <h2 className="text-2xl tracking-wider leading-10 text-black text-center mb-5 max-md:text-lg" style={{ fontFamily: "Playfair Display" }}>
        What is NOSTAi for?
      </h2>
      <p className="text-lg tracking-wide leading-8 text-black text-center mb-16 max-md:text-base" style={{ fontFamily: "Playfair Display" }}>
        NOSTAi is for those who value memories. <br />
        It's for those who want to preserve their stories with the touch of technology. <br />
        For people who believe that the best memories deserve to be crafted with care. <br />
        They know, a thoughtful design brings more than nostalgia—it creates lasting connections.
      </p>

      {/* Quilt Calculator Section */}
      <h2 id="quilt-calculator" className="text-2xl tracking-wider leading-10 text-black text-center mb-8 max-md:text-lg" style={{ fontFamily: "Playfair Display" }}>
        Memory Quilt Calculator
      </h2>
      <div className="mb-16">
        <QuiltCalculatorPreview />
      </div>

      {/* FAQ Section */}
      <h2 id="help-guide" className="text-2xl tracking-wider leading-10 text-black text-center mb-8 max-md:text-lg" style={{ fontFamily: "Playfair Display" }}>
        Frequently Asked Questions
      </h2>
      <div className="space-y-4 mb-16">
        {faqData.map((faq, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <button
              className="w-full px-6 py-4 text-left flex justify-between items-center bg-white hover:bg-gray-50 transition-colors"
              onClick={() => toggleQuestion(index)}
            >
              <span className="text-lg font-medium" style={{ fontFamily: "Playfair Display" }}>
                {faq.question}
              </span>
              {openIndex === index ? (
                <ChevronUp className="w-5 h-5 text-gray-500" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </button>
            <div
              className={`px-6 overflow-hidden transition-all duration-300 ease-in-out ${
                openIndex === index ? 'py-4' : 'max-h-0'
              }`}
            >
              <div className="text-gray-600 leading-relaxed" style={{ fontFamily: "Playfair Display" }}>
                {faq.answer}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AboutSection;